import {
  BoxGeometry,
  Matrix4,
  Quaternion,
  Vector3,
} from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { addVehicleBoundary } from './util.js';
import { ROAD, STATION_ACCESS, STATIONS } from './route.js';

const UP = new Vector3(0, 1, 0);
const UNIT = new Vector3(1, 1, 1);

const WALL_HEIGHT = 1.3;
const WALL_THICKNESS = 0.7;
const WALL_LATERAL =
  ROAD.sidewalkOffset + ROAD.sidewalkWidth / 2 + WALL_THICKNESS / 2;
const WALL_SEGMENT = 6;
const END_INSET = 8;
const ROADBLOCK_INSET = 40;
const GAP_CLEARANCE = 0.4;
const ENDCAP_THICKNESS = 1;

/**
 * Invisible vehicle-only wall around the playable corridor.
 *
 * The barrier sits on the outer sidewalk edge, follows both sides of the
 * street and closes the two route ends. The only openings are the four station
 * driveways, whose geometry comes from STATION_ACCESS. It is one static trimesh
 * on its own collision group, so it costs one rigid body and cannot affect the
 * chase camera or scripted traffic.
 */
export function buildClosedWorld(level, route) {
  const boxes = [];
  const stationGaps = [];

  for (const station of STATIONS) {
    const side = station.side ?? 1;
    for (const offset of STATION_ACCESS.drivewayOffsets) {
      const centre = station.s - offset;
      const half = STATION_ACCESS.drivewayWidth / 2 + GAP_CLEARANCE;
      stationGaps.push({ side, start: centre - half, end: centre + half });
    }
  }

  for (const side of [-1, 1]) {
    const gaps = stationGaps.filter((gap) => gap.side === side);
    const runs = subtractGaps(END_INSET, route.length - END_INSET, gaps);
    for (const [start, end] of runs) {
      appendWallRun(boxes, route, side, start, end);
    }
  }

  appendEndCap(boxes, route, END_INSET);
  appendEndCap(boxes, route, route.length - END_INSET);
  // Full-width vehicle barriers exactly at the police barricades. The visible
  // barricade colliders leave the median and outer shoulders open; these
  // invisible walls close those gaps without affecting scripted traffic.
  appendEndCap(boxes, route, ROADBLOCK_INSET);
  appendEndCap(boxes, route, route.length - ROADBLOCK_INSET);

  const geometry = mergeGeometries(boxes, false);
  for (const box of boxes) box.dispose();
  if (!geometry) throw new Error('Closed world: failed to build the boundary mesh.');

  addVehicleBoundary(level, { geometry, name: 'street-containment' });

  return {
    triangles: geometry.index ? geometry.index.count / 3 : 0,
    stationGaps: stationGaps.length,
    roadblocks: 2,
    roadblockInset: ROADBLOCK_INSET,
    wallHeight: WALL_HEIGHT,
    wallThickness: WALL_THICKNESS,
  };
}

function subtractGaps(start, end, gaps) {
  let runs = [[start, end]];

  for (const gap of [...gaps].sort((a, b) => a.start - b.start)) {
    const next = [];
    for (const [runStart, runEnd] of runs) {
      if (gap.end <= runStart || gap.start >= runEnd) {
        next.push([runStart, runEnd]);
        continue;
      }
      if (gap.start > runStart) next.push([runStart, gap.start]);
      if (gap.end < runEnd) next.push([gap.end, runEnd]);
    }
    runs = next;
  }

  return runs.filter(([runStart, runEnd]) => runEnd - runStart > 0.5);
}

function appendWallRun(boxes, route, side, start, end) {
  for (let s = start; s < end; s += WALL_SEGMENT) {
    const length = Math.min(WALL_SEGMENT + 0.4, end - s);
    if (length < 0.5) break;
    const frame = route.frameAt(s + length / 2);
    const position = frame.position
      .clone()
      .addScaledVector(frame.right, side * WALL_LATERAL);
    position.y = WALL_HEIGHT / 2;
    appendBox(boxes, WALL_THICKNESS, WALL_HEIGHT, length, position, frame.yaw);
  }
}

function appendEndCap(boxes, route, s) {
  const frame = route.frameAt(s);
  const position = frame.position.clone();
  position.y = WALL_HEIGHT / 2;
  appendBox(
    boxes,
    WALL_LATERAL * 2 + WALL_THICKNESS,
    WALL_HEIGHT,
    ENDCAP_THICKNESS,
    position,
    frame.yaw,
  );
}

function appendBox(boxes, width, height, depth, position, yaw) {
  const geometry = new BoxGeometry(width, height, depth);
  const matrix = new Matrix4().compose(
    position,
    new Quaternion().setFromAxisAngle(UP, yaw),
    UNIT,
  );
  geometry.applyMatrix4(matrix);
  boxes.push(geometry);
}
